import { useEffect, useMemo, useState } from 'react'
import styled from '@emotion/styled'
import { okClient } from 'src/clients'
import { Levels } from 'src/components/theme'
import { useInterval } from 'src/hooks/use-interval'
import { RefreshAlert } from 'src/components/refresh-checker/alert'
import { browserLogger } from 'src/utils/browser-logger'

type RefreshCheckerProps = {
  children: React.ReactNode
}

const RefreshWrapper = styled.div`
  position: absolute;
  left: 50%;
  top: 76px;
  transform: translate(-50%);
  z-index: ${Levels.adminPanel};
`

const NEW_CLIENT_VERSION_FLAG_KEY = 'NEW_CLIENT_VERSION'
const FIVE_MINUTES = 1000 * 60 * 5
const MISMATCH_THRESHOLD = 3

export const RefreshChecker = (props: RefreshCheckerProps) => {
  const { children } = props
  const [mismatches, setMismatches] = useState(0)

  const currentVersion = useMemo(() => {
    return process.env.VERSION
  }, [])

  const getAPIVersion = async () => {
    const res = await okClient.getStatus()
    return res.version.version
  }

  const checkVersion = async () => {
    // if current version isn't available dont refresh...this shouldn't happen
    if (!currentVersion) {
      browserLogger.debug('no version available for refresh check')
      return
    }
    const apiVersion = await getAPIVersion()

    // App is the latest version; do nothing
    if (currentVersion === apiVersion) {
      return
    }
    // App is not the latest version; increment mismatch count
    setMismatches((mismatches) => {
      browserLogger.info(`version mismatch detected: ${currentVersion} !== ${apiVersion}`)
      if (mismatches === MISMATCH_THRESHOLD) {
        return mismatches
      }
      return mismatches + 1
    })
    localStorage.setItem(NEW_CLIENT_VERSION_FLAG_KEY, apiVersion)
  }

  useInterval(checkVersion, mismatches !== MISMATCH_THRESHOLD ? FIVE_MINUTES : null)

  useEffect(() => {
    const newClient = localStorage.getItem(NEW_CLIENT_VERSION_FLAG_KEY)
    if (newClient !== null) {
      localStorage.clear()
    }
  }, [])

  return (
    <>
      {mismatches === MISMATCH_THRESHOLD && <RefreshWrapper>{<RefreshAlert />}</RefreshWrapper>}
      {children}
    </>
  )
}
